import { Component, inject, Input, OnInit } from '@angular/core';
import { ArticleDetail } from '../../../core/declarations/article-detail.interface';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { CommentItemComponent } from '@bling-fe/shared/feature-comments';
import { firstValueFrom, Observable } from 'rxjs';
import { User } from '../../../core/declarations/user.interface';
import { select, Store } from '@ngrx/store';
import { UserState } from '../../../core/states/user-state/user.reducers';
import { getCurrentUser } from '../../../core/states/user-state/user.selectors';
import { CommentState } from '../../../core/states/comment-state/comment.state';
import { getEditingComment } from '../../../core/states/comment-state/comment.selectors';
import {
  OptionsModalAction,
  SharedModalsUiOptionsModalComponent
} from '../../../modals/shared-modals-ui-options-modal/shared-modals-ui-options-modal.component';
import { ModalService } from '@bling-fe/shared/modals/base-modal';
import { marker as _ } from '@bling-fe/shared/utils';
import { SetEditingComment } from '../../../core/states/comment-state/comment.actions';
import { Comment } from '../../../core/declarations/comment.interface';
import { ReportService } from '../../services/report.service';
import { CommentRepository } from '../../repositories/comment.repository';
import { PostService } from '../../services/post.service';
import { DirectivesModule } from '../../directives/directives.module';

@Component({
  selector: 'fl-comments',
  imports: [CommentItemComponent, NgFor, NgIf, AsyncPipe, DirectivesModule],
  template: `
    <div class="px-4 py-5">
      <ng-container *ngFor="let comment of post.comments">
        <ng-container
          *ngIf="!comment.hidden && !deletedIds.includes(comment.id)"
        >
          <bl-comment-item
            flResolveLinkTitles
            [id]="comment.id"
            [author]="comment.user"
            [body]="comment.body"
            [createdAt]="comment.created_at"
            [isEditing]="(editingComment$ | async)?.id === comment.id"
            (optionsClick)="showAndHandleOptionsModal($event)"
          ></bl-comment-item>
        </ng-container>
      </ng-container>
    </div>
  `,
  styles: `
    :host {
      bl-comment-item {
        padding-bottom: 1rem;

        &:last-child {
          padding-bottom: 0;
        }
      }
    }
  `
})
export class CommentsComponent implements OnInit {
  @Input() post: ArticleDetail;

  userStore = inject(Store<UserState>);
  commentStore = inject(Store<CommentState>);
  modalService = inject(ModalService);
  reportService = inject(ReportService);
  commentRepository = inject(CommentRepository);
  postService = inject(PostService);

  currentUser$: Observable<User>;
  editingComment$: Observable<Comment>;

  // Track which comments have been deleted to remove them from the view.
  deletedIds: number[] = [];

  ngOnInit() {
    this.currentUser$ = this.userStore.pipe(select(getCurrentUser));
    this.editingComment$ = this.commentStore.pipe(select(getEditingComment));
  }

  /**
   * Show the options modal and handle the actions.
   * Event payload contains `id` which is post's id that was clicked and what actions are available.
   */
  async showAndHandleOptionsModal(payload: { id: number }) {
    const comment = this.post.comments.find(c => c.id === payload.id);
    const currentUser = await firstValueFrom(this.currentUser$);

    const enabledActions = this.getAllowedActions(comment, currentUser);

    const res = await this.modalService.presentAndGetResult(
      SharedModalsUiOptionsModalComponent,
      {
        title: _('MODALS.OPTIONS.COMMENT.TITLE'),
        deleteModalProps: {
          title: _('MODALS.OPTIONS.COMMENT.DELETE_TITLE'),
          description: _('MODALS.OPTIONS.COMMENT.DELETE_DESCRIPTION')
        },
        flagModalProps: {
          title: _('MODALS.OPTIONS.COMMENT.REPORT.TITLE'),
          confirmLabel: _('MODALS.OPTIONS.COMMENT.REPORT.REPORT')
        },
        blockModalProps: {
          title: _('MODALS.OPTIONS.COMMENT.BLOCK.TITLE'),
          confirmLabel: _('MODALS.OPTIONS.COMMENT.BLOCK.BLOCK')
        },
        actionsEnabled: enabledActions
      }
    );

    if (!res) {
      return;
    }

    switch (res.action) {
      case 'edit': {
        const comment = this.post.comments.find(c => c.id === payload.id);
        this.commentStore.dispatch(new SetEditingComment(comment));
        break;
      }
      case 'delete': {
        const comment = this.post.comments.find(c => c.id === payload.id);
        this.sendDeleteRequest(comment);
        break;
      }
      case 'deleteWithReason': {
        const comment = this.post.comments.find(c => c.id === payload.id);
        this.sendDeleteRequest(comment, res.reason_for_action);
        break;
      }
      case 'flag':
        this.reportService.handleReport({
          itemId: payload.id,
          itemType: 'Comment',
          reportType: 'flag',
          reason: res.reason_for_action
        });
        break;
      case 'block':
        this.reportService.handleReport({
          itemId: payload.id,
          itemType: 'Comment',
          reportType: 'block',
          reason: res.reason_for_action
        });
        break;
    }
  }

  private getAllowedActions(comment: Comment, currentUser: User) {
    const isCommentOwner = currentUser.id === comment.user.id;
    const isAdmin = currentUser.super_admin || currentUser.admin;

    const actions: OptionsModalAction[] = [];

    if (isCommentOwner) {
      actions.push('edit', 'delete');
    } else {
      actions.push('flag', 'block');
      if (isAdmin) {
        actions.push('deleteWithReason');
      }
    }

    return actions;
  }

  private sendDeleteRequest(comment: Comment, reasonMessage?: string) {
    this.commentRepository
      .deleteComment(comment, { reason_for_deletion: reasonMessage })
      .subscribe(() => {
        this.deletedIds.push(comment.id);
        this.postService.commentDeleted(comment.article_id);
      });
  }
}
